import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Inject, Injectable} from '@angular/core';
import { PagedResult, Success } from '@unleashed/models/api';
import { getSessionAttributionHeaders, UB_CLIENT } from './client-attribution';

export interface HttpRequestOptions {
  headers?: HttpHeaders | {
    [header: string]: string | string[];
  };
  body?: any;
  observe?: 'body';
  params?: HttpParams | {
    [param: string]: string | string[];
  };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

const UaAuthenticatedWith = 'Ua-Authenticated-With';
const UrbanAirBearer = 'urban-air-bearer';

@Injectable({providedIn: 'root'})
export class ApiService {

  constructor(public http: HttpClient, @Inject(UB_CLIENT) private ubClient: string) {
  }

  getFile(path: string): Observable<Blob> {
    return this.http.get(path, {responseType: 'blob'});
  }

  get<TResponse>(path: string, options?: HttpRequestOptions): Observable<TResponse> {
    if (options?.headers !== undefined && options?.headers !== null) {
      options = {...options, headers: this.appendAuthHeader(options.headers)};
    }
    return this.wrapRequest(this.http.get<Success<TResponse>>(path, options));
  }

  getPagedData<TResponse>(path: string, options?: HttpRequestOptions): Observable<PagedResult<TResponse>> {
    return this.http.get<PagedResult<TResponse>>(path, options);
  }

  delete<TResponse, TBody = Partial<TResponse>>(
    path: string,
    body?: TBody,
    options?: HttpRequestOptions): Observable<TResponse> {
    return this.wrapRequest<TResponse>(
      this.http.request<Success<TResponse>>('delete', path, {body, params: options ? options.params : undefined})
    );
  }

  post<TResponse, TBody = Partial<TResponse>>(
    path: string,
    body: TBody | null,
    options?: HttpRequestOptions): Observable<TResponse> {
    if (options?.headers !== undefined && options?.headers !== null) {
      options = {...options, headers: this.appendAuthHeader(options.headers)};
    }
    return this.wrapRequest<TResponse>(
      this.http.post<Success<TResponse>>(path, body, options)
    );
  }

  put<TResponse, TBody = TResponse>(
    path: string,
    body: TBody | null,
    options?: HttpRequestOptions): Observable<TResponse> {
    if (options?.headers !== undefined && options?.headers !== null) {
      options = {...options, headers: this.appendAuthHeader(options.headers)};
    }
    return this.wrapRequest<TResponse>(
      this.http.put<Success<TResponse>>(path, body, options)
    );
  }

  // Spreading an HttpHeaders instance (e.g. {...someHttpHeaders}) only copies its internal
  // private fields, not the actual header entries, which silently corrupts headers such as
  // Authorization. Normalize to an HttpHeaders instance and use .set() so both HttpHeaders
  // instances and plain header objects are merged correctly.
  private appendAuthHeader(headers: HttpRequestOptions['headers']): HttpHeaders {
    let httpHeaders = headers instanceof HttpHeaders ? headers : new HttpHeaders(headers);
    httpHeaders = httpHeaders.set(UaAuthenticatedWith, UrbanAirBearer);

    // Attribution: which app is calling, plus the session's referrer/campaign/key (first-touch).
    httpHeaders = httpHeaders.set('x-ub-client', this.ubClient);
    const attribution = getSessionAttributionHeaders();
    Object.keys(attribution).forEach(key => {
      httpHeaders = httpHeaders.set(key, attribution[key]);
    });

    return httpHeaders;
  }

  private wrapRequest<TResponse>(request: Observable<Success<TResponse>>): Observable<TResponse> {
    return request.pipe(
      map(response => {
        return response?.data as TResponse;
      }));
  }
}
